import React from 'react'
import { RiCalendarLine } from 'react-icons/ri'
import { FaUserShield, FaClipboardList, FaUser } from 'react-icons/fa'
import type { Autorizador, Processo } from '../../../../models'
import { UserAvatar } from '../../../../components/UserAvatar'
import { format } from 'date-fns'

interface AutorizadorViewCardProps {
  autorizador: Autorizador
  onNavigateToProcesso?: (processo: Processo) => void
}

export const AutorizadorViewCard: React.FC<AutorizadorViewCardProps> = ({
  autorizador,
  onNavigateToProcesso
}) => {
  const totalProcessos = autorizador.processos?.length || 0

  return (
    <div className="p-4 sm:p-6">
      {/* Layout Responsivo - Grid Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Coluna Esquerda */}
        <div className="space-y-4 sm:space-y-5">
          {/* Informações Básicas */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <FaUserShield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">Informações do Autorizador</h3>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Nome</label>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100">{autorizador.nome}</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  autorizador.active
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
                }`}>
                  {autorizador.active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
          </div>

          {/* Usuário Vinculado */}
          <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <FaUser className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">Usuário Vinculado</h3>
            </div>
            
            {autorizador.usuario ? (
              <div className="flex items-center gap-3 p-2 sm:p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800">
                <UserAvatar user={autorizador.usuario} size="md" className="flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {autorizador.usuario.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                    {autorizador.usuario.cargo_usuario}
                  </p>
                  {autorizador.usuario.email && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {autorizador.usuario.email}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-2 sm:p-3 rounded-lg bg-gray-50 dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600">
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic">Usuário não encontrado</p>
              </div>
            )}
          </div>
        </div>

        {/* Coluna Direita - placeholder para futuras extensões */}
        <div className="space-y-4 sm:space-y-5">
          {/* Espaço reservado para futuras funcionalidades */}
        </div>
      </div>

      {/* Seções de largura total - abaixo das colunas */}
      <div className="space-y-4 sm:space-y-5">
        {/* Processos Relacionados */}
        {totalProcessos > 0 ? (
          <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <FaClipboardList className="w-4 h-4 text-green-600 dark:text-green-400" />
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">
                Processos Relacionados ({totalProcessos})
              </h3>
            </div>
            
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Este autorizador é responsável por autorizar demandas dos seguintes processos:
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent">
              {autorizador.processos!.map((processo) => {
                // Verificar se tem pivot para pegar a ordem
                const pivot = (processo as any).pivot as { ordem?: number | null } | undefined
                const ordem = pivot?.ordem

                return onNavigateToProcesso ? (
                  <button
                    key={processo.id}
                    onClick={() => onNavigateToProcesso(processo)}
                    className="flex items-center gap-3 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 transition-all w-full text-left group"
                  >
                    {ordem !== null && ordem !== undefined && (
                      <div className="w-6 h-6 rounded-full bg-purple-600 dark:bg-purple-500 text-white flex items-center justify-center font-semibold text-xs flex-shrink-0">
                        {ordem}
                      </div>
                    )}
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <FaClipboardList className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-green-600 dark:group-hover:text-green-400">
                        {processo.nome_processo}
                      </p>
                      {processo.descricao && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {processo.descricao.substring(0, 40)}...
                        </p>
                      )}
                      {ordem !== null && ordem !== undefined && (
                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                          Ordem {ordem} (Sequencial)
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-green-500 dark:text-green-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      Ver
                    </span>
                  </button>
                ) : (
                  <div
                    key={processo.id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                  >
                    {ordem !== null && ordem !== undefined && (
                      <div className="w-6 h-6 rounded-full bg-purple-600 dark:bg-purple-500 text-white flex items-center justify-center font-semibold text-xs flex-shrink-0">
                        {ordem}
                      </div>
                    )}
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <FaClipboardList className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {processo.nome_processo}
                      </p>
                      {ordem !== null && ordem !== undefined && (
                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                          Ordem {ordem} (Sequencial)
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <FaClipboardList className="w-4 h-4" />
              <p className="text-xs sm:text-sm italic">Não está vinculado a nenhum processo</p>
            </div>
          </div>
        )}

        {/* Informações de Auditoria */}
        <div className="bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <RiCalendarLine className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">Informações de Auditoria</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-gray-500 dark:text-gray-400 mb-0.5">ID do Autorizador</p>
              <p className="font-mono font-semibold text-gray-900 dark:text-gray-100">#{autorizador.id}</p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 mb-0.5">Status</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                autorizador.active
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
              }`}>
                {autorizador.active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            {autorizador.created_at && (
              <div>
                <p className="text-gray-500 dark:text-gray-400 mb-0.5">Criado em</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                  {format(new Date(autorizador.created_at!), "dd/MM/yyyy")}
                </p>
              </div>
            )}
            {autorizador.updated_at && (
              <div>
                <p className="text-gray-500 dark:text-gray-400 mb-0.5">Atualizado em</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                  {format(new Date(autorizador.updated_at!), "dd/MM/yyyy")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

